import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp } from "@/lib/motion";
import { TrendingDown, TrendingUp } from "lucide-react";

export default function DeathWindow() {
  return (
    <section id="data" className="relative py-24 md:py-32">
      <div className="container-x">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="eyebrow">The 5-Minute Death Window</span>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[0.95] tracking-tight text-ink md:text-6xl">
            Every minute you wait, the money{" "}
            <span className="grad-text">evaporates.</span>
          </h2>
          <p className="mt-5 text-lg text-ink-soft">
            The data is brutal and unanimous. Speed isn’t a nice-to-have — it is the entire game.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          <CliffChart />
          <LiftChart />
        </div>
      </div>
    </section>
  );
}

function ChartCard({ children, badge, badgeColor, title, source }) {
  return (
    <Reveal variants={fadeUp} className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-card md:p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{source}</p>
          <h3 className="mt-1 font-display text-xl font-semibold text-ink">{title}</h3>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ${badgeColor}`}>{badge}</span>
      </div>
      {children}
    </Reveal>
  );
}

function CliffChart() {
  const line = "M 40,60 L 130,68 L 210,74 L 285,82 C 305,90 320,235 345,255 L 430,268 L 540,276";
  const area = `${line} L 540,300 L 40,300 Z`;
  return (
    <ChartCard
      source="Harvard Business Review"
      title="Qualification Odds vs. Response Time"
      badge={<><TrendingDown size={15} /> -400%</>}
      badgeColor="bg-red-50 text-red-600"
    >
      <div className="relative">
        <svg viewBox="0 0 580 320" className="w-full">
          <defs>
            <linearGradient id="cliffArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#4F46E5" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* grid */}
          {[80, 140, 200, 260].map((y) => (
            <line key={y} x1="40" y1={y} x2="540" y2={y} stroke="#EEF2F6" strokeWidth="1" />
          ))}
          {/* 5-minute marker */}
          <motion.line
            x1="300" y1="30" x2="300" y2="300"
            stroke="#EF4444" strokeWidth="1.5" strokeDasharray="5 5"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.1 }}
          />
          <motion.text
            x="306" y="46" fill="#EF4444" fontSize="13" fontWeight="700"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.2 }}
          >5 MIN</motion.text>
          {/* area */}
          <motion.path d={area} fill="url(#cliffArea)"
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.4, duration: 0.8 }} />
          {/* line drawing left-to-right */}
          <motion.path
            d={line} fill="none" stroke="#4F46E5" strokeWidth="3.5" strokeLinecap="round"
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
            transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.circle cx="40" cy="60" r="5" fill="#4F46E5"
            initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} />
          {/* x labels */}
          {[[40,"0"],[130,"1m"],[210,"2m"],[300,"5m"],[430,"10m"],[540,"30m"]].map(([x,l]) => (
            <text key={l} x={x} y="316" fill="#94A3B8" fontSize="11" textAnchor="middle">{l}</text>
          ))}
        </svg>
        <div className="pointer-events-none absolute bottom-8 right-4 max-w-[45%] text-right">
          <p className="font-display text-2xl font-bold text-red-600 md:text-3xl">-400%</p>
          <p className="text-xs font-medium text-ink-muted">Drop in Qualification Odds after 5 minutes</p>
        </div>
      </div>
    </ChartCard>
  );
}

function LiftChart() {
  const bars = [
    { label: "< 60 sec", value: 100, hero: true, tag: "+391%" },
    { label: "< 5 min", value: 46 },
    { label: "< 30 min", value: 22 },
    { label: "< 1 hr", value: 12 },
    { label: "> 1 hr", value: 5 },
  ];
  return (
    <ChartCard
      source="Velocify Benchmark"
      title="Conversion Lift by Speed-to-Lead"
      badge={<><TrendingUp size={15} /> +391%</>}
      badgeColor="bg-accent-soft text-accent-dark"
    >
      <div className="flex h-[300px] items-end justify-between gap-3 md:gap-4">
        {bars.map((b, i) => (
          <div key={b.label} className="flex flex-1 flex-col items-center justify-end">
            {b.hero && (
              <motion.span
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 1.2 }}
                className="mb-2 rounded-full bg-accent px-2.5 py-1 text-xs font-bold text-white shadow-glow"
              >{b.tag}</motion.span>
            )}
            <motion.div
              initial={{ height: 0 }} whileInView={{ height: `${b.value * 2.2}px` }} viewport={{ once: true }}
              transition={{ delay: 0.15 * i, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={`w-full rounded-t-xl ${b.hero ? "bg-gradient-to-t from-accent-dark to-accent-light shadow-glow" : "bg-slate-200"}`}
            />
            <span className={`mt-3 text-center text-[11px] font-medium md:text-xs ${b.hero ? "text-accent-dark" : "text-ink-muted"}`}>{b.label}</span>
          </div>
        ))}
      </div>
      <p className="mt-5 text-center text-sm text-ink-muted">
        Leads engaged in <span className="font-semibold text-accent">under 60 seconds</span> convert dramatically higher than any slower response.
      </p>
    </ChartCard>
  );
}
